import React from 'react';
import PageHero from '../components/ui/PageHero';
import { historyTimeline, agitateurs, siteConfig } from '../data/mock';
import { CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const QuiSommesNousPage = () => {
  return (
    <div>
      <SEO
        title="Agence événementielle Lyon — Notre histoire & équipe"
        description="Découvrez Mon Evénemenciel, agitateurs d'événements depuis 2019. Notre équipe passionnée crée des expériences uniques pour vos collaborateurs. Histoire, valeurs et fondateurs."
        keywords="qui sommes nous, équipe Mon Evénemenciel, Christophe Garnier, Alexis Rausch, agence événementielle Lyon, histoire entreprise"
        url="/qui-sommes-nous"
        image="/img/photo-histoire.webp"
      />
      <PageHero
        title="Qui sommes nous ?"
        subtitle="Découvrez notre histoire et notre équipe"
        backgroundImage="/img/photo-histoire.webp"
      />

      {/* Nos Valeurs Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-display text-brand-petrol text-center mb-2 md:mb-4">
            Nos Valeurs
          </h2>
          <p className="text-center text-[#96452f] font-display italic mb-8 md:mb-12 text-sm md:text-base">
            Ce qui nous anime au quotidien
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* AUDACE */}
            <div className="group relative bg-gradient-to-br from-[#96452f]/5 to-[#96452f]/10 rounded-2xl p-5 md:p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-[#96452f]/20">
              <div className="absolute -top-3 left-4 md:left-6 bg-[#96452f] text-white px-3 md:px-4 py-1 rounded-md text-xs md:text-sm font-bold">
                AUDACE
              </div>
              <h3 className="text-base md:text-lg font-display text-brand-petrol mt-3 md:mt-4 mb-2 md:mb-3">
                L'audace comme moteur
              </h3>
              <p className="text-brand-petrol/70 text-xs md:text-sm leading-relaxed">
                Chez nous, le mot « impossible » a été rayé du dictionnaire. L'audace, c'est notre capacité à trouver des solutions ingénieuses là où d'autres bloquent, tout en garantissant le meilleur rapport qualité-prix. Être votre partenaire, c'est être fiable, agile et toujours prêt à décrocher la lune pour vous.
              </p>
            </div>

            {/* RESPECT */}
            <div className="group relative bg-gradient-to-br from-[#D08154]/5 to-[#D08154]/10 rounded-2xl p-5 md:p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-[#D08154]/20">
              <div className="absolute -top-3 left-4 md:left-6 bg-[#D08154] text-white px-3 md:px-4 py-1 rounded-md text-xs md:text-sm font-bold">
                RESPECT
              </div>
              <h3 className="text-base md:text-lg font-display text-brand-petrol mt-3 md:mt-4 mb-2 md:mb-3">
                Le respect, sans filtre
              </h3>
              <p className="text-brand-petrol/70 text-xs md:text-sm leading-relaxed">
                Pas de relation durable sans confiance. Chez nous, respect rime avec transparence totale et intégrité (promis, pas de mauvaises surprises). Nous restons humains avant tout : on s'écoute, on s'encourage, et si on se trompe, on a le courage de l'admettre pour mieux rebondir. L'intelligence collective, c'est notre force de frappe.
              </p>
            </div>

            {/* SOLIDARITÉ */}
            <div className="group relative bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-5 md:p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-200">
              <div className="absolute -top-3 left-4 md:left-6 bg-gray-700 text-white px-3 md:px-4 py-1 rounded-md text-xs md:text-sm font-bold">
                SOLIDARITÉ
              </div>
              <h3 className="text-base md:text-lg font-display text-brand-petrol mt-3 md:mt-4 mb-2 md:mb-3">
                Solidaires sur toute la ligne
              </h3>
              <p className="text-brand-petrol/70 text-xs md:text-sm leading-relaxed">
                La solidarité, c'est notre carburant pour avancer. Cela signifie privilégier les circuits courts avec des partenaires du coin, agir pour l'environnement et s'ouvrir aux autres. Nous croyons en la force du "faire ensemble" : avec nos équipes, avec nos prestataires, et surtout avec vous.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Histoire Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-display text-brand-petrol text-center mb-2 md:mb-4">
            Notre Histoire
          </h2>
          <p className="text-center text-[#96452f] font-display italic mb-8 md:mb-12 text-sm md:text-base">
            Découvrez comment tout a commencé !
          </p>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-8 top-0 bottom-0 w-0.5 bg-[#D08154]" />

            {historyTimeline.map((item, index) => (
              <div key={index} className="relative pl-12 md:pl-20 pb-10 md:pb-16 last:pb-0">
                {/* Timeline dot */}
                <div className="absolute left-1 md:left-4 top-0 w-6 h-6 md:w-8 md:h-8 bg-[#96452f] rounded-full flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </div>

                {/* Content */}
                <div className="bg-brand-ivory rounded-2xl p-5 md:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <span className="text-[#D08154] font-bold text-sm md:text-lg">
                    {item.year}
                  </span>
                  <h3 className="text-lg md:text-2xl font-display text-brand-petrol mt-1 md:mt-2 mb-2 md:mb-4">
                    {item.title}
                  </h3>
                  <p className="text-brand-petrol/70 leading-relaxed text-sm md:text-base">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Agitateurs Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-display text-brand-petrol text-center mb-2 md:mb-4">
            Les Agitateurs
          </h2>
          <p className="text-center text-[#96452f] font-display italic mb-8 md:mb-12 text-sm md:text-base">
            Qui se cache derrière Mon Evénemenciel ?
          </p>

          <div className="space-y-16 md:space-y-24">
            {agitateurs.map((person, index) => (
              <div
                key={index}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 md:gap-12 items-center`}
              >
                {/* Image */}
                <div className="lg:w-1/3">
                  <div className="relative">
                    <div className="rounded-full overflow-hidden aspect-square shadow-2xl border-4 md:border-8 border-white max-w-[200px] md:max-w-xs mx-auto">
                      <img
                        src={person.image}
                        alt={person.name}
                        className="w-full h-full object-cover"
                       loading="lazy" decoding="async" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="lg:w-2/3 text-center lg:text-left">
                  <h3 className="text-xl md:text-2xl font-display text-brand-petrol mb-1 md:mb-2">
                    {person.name}
                  </h3>
                  {person.role && (
                    <span className="inline-block bg-[#96452f] text-white px-3 py-1 rounded-md text-sm font-medium">
                      {person.role}
                    </span>
                  )}
                  {person.subtitle && (
                    <p className="text-[#D08154] font-display italic text-base md:text-lg mt-3">
                      « {person.subtitle} »
                    </p>
                  )}
                  {person.motto && (
                    <p className="text-gray-500 font-medium italic mt-2 text-sm md:text-base">
                      💡 {person.motto}
                    </p>
                  )}
                  <p className="text-brand-petrol/70 leading-relaxed mt-3 md:mt-4 text-sm md:text-base">
                    {person.description}
                  </p>
                  {person.extras && (
                    <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                      <div className="bg-gradient-to-r from-[#96452f]/10 to-[#96452f]/5 rounded-xl px-4 py-3 border border-[#96452f]/20">
                        <p className="text-xs text-[#96452f] font-bold uppercase tracking-wider mb-1">✨ Son petit plus</p>
                        <p className="text-brand-petrol/80 text-sm font-medium">{person.extras.plus}</p>
                      </div>
                      <div className="bg-gradient-to-r from-[#D08154]/10 to-[#D08154]/5 rounded-xl px-4 py-3 border border-[#D08154]/20">
                        <p className="text-xs text-[#D08154] font-bold uppercase tracking-wider mb-1">🍫 Son péché mignon</p>
                        <p className="text-brand-petrol/80 text-sm font-medium">{person.extras.peche}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuiSommesNousPage;
